import { Grid3x3, Mic, MicOff, PauseCircle, PhoneOff, PlayCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { TwilioCallState } from "@/context/twilio.context";
import { cn } from "@/lib/utils";

type Props = {
  isCallOver: boolean;
  onClose: () => void;
  isMuted: boolean;
  mute: () => void;
  unmute: () => void;
  isOnHold: boolean;
  hold: () => void;
  unhold: () => void;
  state: TwilioCallState;
  onEndCall: () => void;
  showKeypad: boolean;
  onToggleKeypad: () => void;
};

const CallControls = ({
  isCallOver,
  onClose,
  isMuted,
  mute,
  unmute,
  isOnHold,
  hold,
  unhold,
  state,
  onEndCall,
  showKeypad,
  onToggleKeypad,
}: Props) => (
  <div className="relative border-t border-white/5 p-4 bg-black/20">
    {isCallOver ? (
      <Button
        type="button"
        onClick={onClose}
        className="w-full bg-white/10 hover:bg-white/15 text-white font-medium"
      >
        Close
      </Button>
    ) : (
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={isMuted ? unmute : mute}
            disabled={state !== "in-call" || isOnHold}
            className={cn(
              "flex flex-col items-center justify-center gap-1 rounded-xl border px-4 py-3 transition-colors",
              isMuted && !isOnHold
                ? "bg-white/15 border-white/20 text-white"
                : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
              (state !== "in-call" || isOnHold) && "opacity-50 cursor-not-allowed",
            )}
          >
            {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            <span className="text-xs font-medium">{isMuted ? "Unmute" : "Mute"}</span>
          </button>

          <button
            type="button"
            onClick={isOnHold ? unhold : hold}
            disabled={state !== "in-call"}
            className={cn(
              "flex flex-col items-center justify-center gap-1 rounded-xl border px-4 py-3 transition-colors",
              isOnHold
                ? "bg-amber-500/20 border-amber-500/40 text-amber-300"
                : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
              state !== "in-call" && "opacity-50 cursor-not-allowed",
            )}
          >
            {isOnHold
              ? <PlayCircle className="w-5 h-5" />
              : <PauseCircle className="w-5 h-5" />}
            <span className="text-xs font-medium">{isOnHold ? "Resume" : "Hold"}</span>
          </button>

          <button
            type="button"
            onClick={onToggleKeypad}
            disabled={state !== "in-call"}
            aria-pressed={showKeypad}
            className={cn(
              "flex flex-col items-center justify-center gap-1 rounded-xl border px-4 py-3 transition-colors",
              showKeypad
                ? "bg-white/15 border-white/20 text-white"
                : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
              state !== "in-call" && "opacity-50 cursor-not-allowed",
            )}
          >
            <Grid3x3 className="w-5 h-5" />
            <span className="text-xs font-medium">Keypad</span>
          </button>
        </div>

        <button
          type="button"
          onClick={onEndCall}
          className="flex items-center justify-center gap-2 w-full rounded-xl bg-red-500 hover:bg-red-600 active:bg-red-700 text-white font-semibold py-3 shadow-lg shadow-red-500/20 transition-colors"
        >
          <PhoneOff className="w-5 h-5" />
          End call
        </button>
      </div>
    )}
  </div>
);

export default CallControls;
